import { type TemplateResult, LitElement, html, svg, type PropertyValues } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import Glide, { Anchors, Breakpoints, Images, Swipe } from '@glidejs/glide/dist/glide.modular.esm.js';
import styles from './cp-slider.css';
import { property } from 'lit/decorators/property.js';
import '@patternfly/elements/pf-button/pf-button.js';
import { classMap } from 'lit/directives/class-map.js';
import { state } from 'lit/decorators/state.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ComposedEvent } from '@patternfly/pfe-core/core.js';
import { queryAll } from 'lit/decorators/query-all.js';
import { query } from 'lit/decorators/query.js';

export class PageChangeEvent extends ComposedEvent {
  constructor(public page: number, public totalPages: number, public event?: Event) {
    super('page-change');
  }
}

const REQUIRED_OPTIONS: Partial<Glide.Options> = { type: 'slider', swipeThreshold: false, dragThreshold: false, waitForTransition: false };

const ICONS = {
  ['previous-page']: {
    path: 'M5.3865 16.2533C5.31984 16.0933 5.31984 15.9067 5.3865 15.7467C5.41984 15.6667 5.4665 15.5933 5.53317 15.5267L13.5332 7.52667C13.7932 7.26667 14.2132 7.26667 14.4732 7.52667C14.7332 7.78667 14.7332 8.20667 14.4732 8.46667L7.61317 15.3267H25.9998C26.3665 15.3267 26.6665 15.6267 26.6665 15.9933C26.6665 16.36 26.3665 16.66 25.9998 16.66H7.6065L14.4665 23.52C14.7265 23.78 14.7265 24.2 14.4665 24.46C14.3332 24.5933 14.1665 24.6533 13.9932 24.6533C13.8198 24.6533 13.6532 24.5867 13.5198 24.46L5.51984 16.46C5.45984 16.4 5.4065 16.3267 5.37317 16.24L5.3865 16.2533Z',
  },
  ['next-page']: {
    path: 'M26.6135 16.2533C26.6802 16.0933 26.6802 15.9067 26.6135 15.7467C26.5802 15.6667 26.5335 15.5933 26.4668 15.5267L18.4668 7.52667C18.2068 7.26667 17.7868 7.26667 17.5268 7.52667C17.2668 7.78667 17.2668 8.20667 17.5268 8.46667L24.3868 15.3267H6.00016C5.6335 15.3267 5.3335 15.6267 5.3335 15.9933C5.3335 16.36 5.6335 16.66 6.00016 16.66H24.3935L17.5335 23.52C17.2735 23.78 17.2735 24.2 17.5335 24.46C17.6668 24.5933 17.8335 24.6533 18.0068 24.6533C18.1802 24.6533 18.3468 24.5867 18.4802 24.46L26.4802 16.46C26.5402 16.4 26.5935 16.3267 26.6268 16.24L26.6135 16.2533Z',
  },
  get(name: 'previous-page' | 'next-page') {
    const { path } = ICONS[name];
    return svg`
      <svg aria-hidden="true"
           width="32"
           height="32"
           fill="currentColor"
           style="vertical-align: middle;"
           viewBox="0 0 32 32">
        <path d="${path}"/>
      </svg>`;
  },
};

/**
 * A slider presents a set of items, referred to as slides, by displaying a subset of one or more slides at a time.
 *
 * @summary Displays a subset of one or more slides at a time
 *
 * @slot    slide-*               - Individual slide where * is replaced with the slide number
 *
 * @csspart container             - Slider container
 * @csspart controls              - Controls container
 * @csspart pagination            - Page navigation container
 * @csspart page-control          - Specific page buttons
 * @csspart previous-page-control - Previous page button
 * @csspart next-page-control     - Next page button
 * @csspart track                 - Slides track
 * @csspart slides                - List of slides
 *
 * @cssprop {length} --cp-slider-control-width
 *          Controls the size of the previous and next button
 *          {@link --rh-space-3xl}
 *          {@default `48px`}
 *
 * @cssprop {color} --cp-slider-control-color-page-navigation
 *          Color of the navigation page buttons
 *          {@link --rh-color-gray-10}
 *          {@default `#e0e0e0`}
 */
@customElement('cp-slider')
export class CpSlider extends LitElement {
  static readonly styles = [styles];

  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true, composed: true };

  // @todo: KS work with rhds designers to come up with focus styles for slider

  /** Whether to show or hide page navigation elements */
  @property({ type: Boolean, attribute: 'hide-pagination' }) hidePagination?: boolean;

  /**
   * Glide slider options. Option values must not be null or undefined.
   * https://glidejs.com/docs/options/
   */
  @property({ type: Object }) options?: Partial<Glide.Options>;

  /** Required accesible label for the slider */
  @property() label?: string;

  /** Required accesible name for each slide's visually hidden control text*/
  // 'Slide' or 'Panel' - Panel used when a slider is a multi-slider
  @property() hiddenTxt?: string = 'Slide';

  @property() totalPgLabel?: number = this.totalPages;

  @query('#previous-page-control') private prevBtn!: HTMLButtonElement;

  @query('#next-page-control') private nextBtn!: HTMLButtonElement;

  @query('.page-control.active') private activePgCtrl!: HTMLButtonElement;

  @queryAll('.page-control:not(.active)') private inactivePgCtrls!: HTMLButtonElement[];

  @queryAll('.page-control') private allPgCtrls!: HTMLButtonElement[];

  @queryAll('.slide') private allSlides!: HTMLElement[];

  /** Required A11y roles for carousel sliders */
  // See @resource:
  // https://www.w3.org/WAI/ARIA/apg/patterns/carousel/
  /**
   * @property {string} 'aria-roledescription="carousel"' - always set to "carousel" when applied to the CP Slider custom tag
   * @property {string} 'aria-roledescription="slide"' - always set to "slide" when applied to the individual shadowDom slide container
   * @property {string} 'role="group"' - set role on shadow slide to group if there is no pagination
   * @property {string} 'role="tablist"' - set role on shadow slide to tablist if there is pagination
   * @property {string} 'tabindex="-1"' - set tabindex -1 on inactive page controls and inactive slides
   * @property {string} 'tabindex="0"' - set tabindex 0 on active page control and active slide or active slides for multi-sliders
   * @property {string} 'aria-selected="true"' - set to true on the page control button when the page control is active
   * @property {string} 'aria-selected="false"' - set to false on the page control button when the page control is inactive
   * @property {string} 'aria-controls="SLIDE_ID"' - set aria-controls attr on each slider control which references the corresponding slide by id
   * @property {string} 'aria-live="polite"' - set on the live region container - polite does not interupt the user too much
   * @property {string} 'role="status"' - set on the live region container - makes sure that the aria-live message is a status message
   */

  get page() {
    return this.#page;
  }

  set page(newPage: number) {
    if (newPage > this.totalPages) {
      this.#page = 1;
    } else if (newPage < 1) {
      this.#page = this.totalPages;
    } else {
      this.#page = newPage;
    }
    this.requestUpdate();
  }

  get perPage() {
    return this._glide?.settings?.perView || 1;
  }

  get totalPages() {
    return Math.ceil(this.children.length / this.perPage);
  }

  @state() _glide?: Glide;

  #page = 1;

  protected willUpdate(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('options') && this.options) {
      // Sync options change with Glide instance
      this._glide?.update(this.#glideOptions());
    }
  }

  render() {
    // @todo: Plain html Button needs to be switched back to pf-button once this repo is using the newest pfe version which includes the major a11y fix for pf-button
    return html`
      <section id="container" 
               aria-roledescription="carousel"
               aria-label=${ifDefined(this.label)}
               part="container">
        <pf-button id="previous-page-control"
               label="Previous slide"
               aria-controls="slides"
               variant="tertiary"
               part="previous-page-control"
               ?hidden=${this.page === 1}
               @click=${(event: Event) => this.onPageChange(event, this.page - 1)}>
                ${ICONS.get('previous-page')}
        </pf-button>        
        <div
          id="track"
          class=${classMap({ ['slide-preview']: !!this._glide?.settings.peek })}
          data-glide-el="track"
          part="track">
          <div id="slides" part="slides">
            <!-- https://github.com/glidejs/glide/issues/310 -->
            ${[...this.children].reduce((result, _, index) => [...result, html`
              <div class="slide" data-ui="slide" part="slide" aria-roledescription="slide" role="tabpanel" id="slide-${index + 1}" aria-label=${`${index + 1} of ${this.totalPgLabel}`}>
                <slot name="slide-${index + 1}" class="slide" @slotchange=${this.#onSlotchange}></slot>
              </div>`], [] as TemplateResult[])}
          </div>
        </div>
        <pf-button id="next-page-control"
              label="Next slide"
              aria-controls="slides"
              variant="tertiary"
              part="next-page-control"
              ?hidden=${this.totalPages === 1}
              @click=${(event: Event) => this.onPageChange(event, this.page + 1)}>
              ${ICONS.get('next-page')}
          </pf-button>
        <div id="controls" 
             part="controls">
          <div id="pagination"
               aria-label="Slides"
               role="tablist"
               part="pagination"
               ?hidden=${!!this.hidePagination || this.totalPages === 1}>
            ${[...Array(this.totalPages)].map((_, index) => html`
                <button role="tab" class=${classMap({ ['page-control']: true, active: this.page === index + 1 })} 
                part="page-control"
                aria-controls="slide-${index + 1}"
                plain 
                @click=${(event: Event) => {
                  this.onPageChange(event, index + 1);
                }}><span class="visually-hidden">${`${this.hiddenTxt} ${index + 1}`}</span></button>`)}
          </div>
        </div>
      </section>
      <p id="live-region-msg" class="visually-hidden" role="status" aria-live="polite"></p>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeydown);
  }

  protected firstUpdated() {
    // initialize Glide instance
    const root = this.shadowRoot?.querySelector('#container') as HTMLElement;
    if (root) {
      this._glide = new Glide(root, this.#glideOptions()).mount({ Anchors, Breakpoints, Images, Swipe });
    }
    // Ensures that keyboard and screen reader users tab to the active control once they tab away from the active slide
    // The rest of the pg controls are set to tabindex -1 because we only want the first active control accessible by tab, the rest will be focused using the left and right arrow keys once the user tabs into the pagination
    // Set tabindex to 0 if active || set tabindex to -1 if not active
    this.#toggleTabindex();
    // Set aria-selected=false on inactive page controls and set aria-selected=true on active page control
    this.#toggleAriaSelected();
  }

  protected updated() {
    // toggle slide visibility from assitive technology
    this.#toggleVisible();
    // determine if slider has more than one active slide
    this.#isMultiSlider();
    // determine if slider has no pagination
    this.#noPagination();
    // set tabindex to 0 if active || set tabindex to -1 if not active
    // set class to 'cp-slider active' if active || set class to 'cp-slider' if not active
    this.#toggleTabindex();
    // Set aria-selected=false on inactive page controls and set aria-selected=true on active page control
    this.#toggleAriaSelected();

    // the live region message div and the button that activates the message update must both be in the shadowDom or both be in the lightDom to work
    // the message will not play for the screen reader if one element is in the shadowDom and one element is in the lightDom
    this.prevBtn.addEventListener('click', () => {
      this.#setLiveRegionMsg();
    });
    this.nextBtn.addEventListener('click', () => {
      this.#setLiveRegionMsg();
    });
  }

  /**
   * Dispatch an event went the page is changed
   * @param event the event that trigged the change
   * @param page the new page number
   */
  onPageChange(event: Event, page: number) {
    this.goToPage(page);
    this.dispatchEvent(new PageChangeEvent(this.page, this.totalPages, event));
  }

  /**
   * Navigate to a specific page. If the page does not exist, it will navigate to the first page.
   * @param page the new page number
   */
  goToPage(page: number) {
    this.page = page;
    this._glide?.go(`=${this.perPage * (this.page - 1)}`);
  }

  /**
   * Merges user-defined and required options for Glide
   * @returns Glide options object
   */
  #glideOptions(): Partial<Glide.Options> {
    return { ...this.options, ...REQUIRED_OPTIONS };
  }

  /**
   * Hide slides inactive slides from assistive technology
   */
  #toggleVisible() {
    const slides = this?.querySelectorAll('[slot*="slide-"]');
    const end = this.page * this.perPage;
    const start = end - this.perPage;

    if (slides) {
      // needed for setting proper lightdom styles on slide elements
      slides.forEach(slide => {
        slide.classList.add('cp-slide');
      });

      slides.forEach((slide, index) => {
        if (index >= start && index < end) {
          slide.setAttribute('tabindex', '0');
          slide.classList.add('active-slide');
        } else {
          slide.setAttribute('tabindex', '-1');
          slide.classList.remove('active-slide');
        }
      });
    }
  }

  #onSlotchange() {
    // toggle slide visibility from assitive technology
    this.#toggleVisible();
    // dispatch initial page and totalPages
    this.dispatchEvent(new PageChangeEvent(this.page, this.totalPages));
    this.requestUpdate();
  }

  /**
   * Determine if slider is multiple slides per page, if yes then set custom attribute "multi-slider" on the slider
   * If the slider has multiple active slides per panel then a11y updates must be made to the page control alt text to reflect that
   */
  #isMultiSlider() {
    // @todo KS investigate error
    // @ts-expect-error: KS investigate error
    function getChildTextArray(parentElement) {
      // eslint-disable-next-line
      const children = parentElement.children;
      const textArray = [];

      // @todo KS investigate error
      // eslint-disable-next-line
      for (let i = 0; i < children.length; i++) {
        textArray.push(children[i].className);
      }
      return textArray;
    }

    const childText = getChildTextArray(this);

    const newArr = childText;

    const words = 'cp-slide active-slide';
    // @todo KS investigate error
    // @ts-expect-error: KS investigate error
    function countWordOccurrences(arr, words) {
      let count = 0;
      // @todo KS investigate error
      // eslint-disable-next-line
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] === words) {
          count++;
        }
      }
      return count;
    }

    const occurrences = countWordOccurrences(newArr, words);

    if (occurrences > 1) {
      this.setAttribute('multi-slider', '');

      this.hiddenTxt = 'Panel';

      // make sure pagination cannot be used when there is more than 2 active slides per panel to avoid major a11y issues
      if (occurrences > 2) {
        // if implementor already added the attr then do nothing
        if (this.hasAttribute('hide-pagination')) {
          return;
          // else add the attribute
        } else {
          this.setAttribute('hide-pagination', '');
        }
      }
    } else {
      this.removeAttribute('multi-slider');
    }
  }

  /**
   * Update aria roles to group when pagination is hidden
   */
  #noPagination() {
    // if Slider on has one page set hide-pagination attribute on it
    if (this.totalPages === 1) {
      this.setAttribute('hide-pagination', '');
    }
    // if Slider has no pagination update role to reflect that
    if (this.hasAttribute('hide-pagination')) {
      this.allSlides?.forEach(element => {
        element.setAttribute('role', 'group');
      });
    }
  }

  /**
   * Toggle tabindex as active page control changes
   */
  #toggleTabindex() {
    this.allPgCtrls.forEach(element => {
      if (element.classList.contains('active')) {
        element.setAttribute('tabindex', '0');
      } else {
        element.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * Toggle aria-selected true on active page control and aria-selected false on inactive page controls
   */
  #toggleAriaSelected() {
    // set aria-selected true on active page control
    this.activePgCtrl.setAttribute('aria-selected', 'true');
    // set aria-selected false on inactive page controls
    this.inactivePgCtrls.forEach(element => {
      element.setAttribute('aria-selected', 'false');
    });
  }

  /**
   * Update live region message when user changes slides
   * this message tells screen reader users that the active slide has changed
   */
  // @reference: https://a11y-solutions.stevenwoodson.com/solutions/aria/live/
  #setLiveRegionMsg() {
    // @ts-expect-error: ignore possibly null error
    this.shadowRoot.querySelector('#live-region-msg').textContent = `${this.hiddenTxt} ${this.#page} active`;
  }

  // keyboard interactions
  async #onKeydown(event: KeyboardEvent) {
    // @reference https://developer.mozilla.org/en-US/docs/Web/API/Event/composedPath
    const path = event.composedPath();

    // get element at event target
    const eventElement = event.target as HTMLElement;
    // get closest cp-slider tag
    const parentElementTag = eventElement.closest('cp-slider') as HTMLElement;
    // get the shadow pagination container
    const pagination = this.shadowRoot?.querySelector('#pagination') as HTMLElement;
    // get the shadow previous button
    const prevBtn = this.shadowRoot?.querySelector('#previous-page-control') as HTMLButtonElement;
    // get the shadow next button
    const nextBtn = this.shadowRoot?.querySelector('#next-page-control') as HTMLButtonElement;
    // get the active page control of the cp-slider that is currently in use
    const eventButton = parentElementTag.shadowRoot?.querySelector('.active');

    // set flag to false initially
    // toggle on/off stop propagation and prevent default
    let flag = false;

    switch (event.key) {
      case 'Enter': {
        if (path.includes(prevBtn)) {
          this.onPageChange(event, this.page - 1);
          this.#setLiveRegionMsg();
          flag = true;
        }

        if (path.includes(nextBtn)) {
          this.onPageChange(event, this.page + 1);
          this.#setLiveRegionMsg();
          flag = true;
        }
        break;
      }
      case 'Space': {
        if (path.includes(prevBtn)) {
          this.onPageChange(event, this.page - 1);
          this.#setLiveRegionMsg();
          flag = true;
        }

        if (path.includes(nextBtn)) {
          this.onPageChange(event, this.page + 1);
          this.#setLiveRegionMsg();
          flag = true;
        }
        break;
      }
      case 'ArrowLeft': {
        if (path.includes(pagination)) {
          this.onPageChange(event, this.page - 1);

          // remove active class and set tabindex=-1 on currently active page control when Home is pressed
          eventButton?.setAttribute('class', 'page-control');
          eventButton?.setAttribute('tabindex', '-1');
          // set aria-selected=false on current active page control when ArrowLeft is pressed
          eventButton?.setAttribute('aria-selected', 'false');

          // wait for slide animation to finish
          setTimeout(() => {
            // The rest of the pg controls are set to tabindex -1 because we only want the active control accessible by tab, the rest will be focused using the left and right arrow keys once the user tabs into the pagination and uses those keys
            // set aria-selected=true on new active page control
            this.activePgCtrl.setAttribute('aria-selected', 'true');
            this.activePgCtrl.focus();
          }, 10);
        }
        flag = true;
        break;
      }
      case 'ArrowRight': {
        if (path.includes(pagination)) {
          this.onPageChange(event, this.page + 1);

          // remove active class and set tabindex=-1 on currently active page control when Home is pressed
          eventButton?.setAttribute('class', 'page-control');
          eventButton?.setAttribute('tabindex', '-1');
          // set aria-selected=false on current active page control when ArrowLeft is pressed
          eventButton?.setAttribute('aria-selected', 'false');

          // wait for slide animation to finish
          setTimeout(() => {
            // The rest of the pg controls are set to tabindex -1 because we only want the active control accessible by tab, the rest will be focused using the left and right arrow keys once the user tabs into the pagination and uses those keys
            // set aria-selected=true on new active page control
            this.activePgCtrl.setAttribute('aria-selected', 'true');
            this.activePgCtrl.focus();
          }, 10);
        }
        flag = true;
        break;
      }

      default: {
        break;
      }
    }
    // stop propagation and add prevent default if flag is true
    // makes sure that when the arrow keys or other buttons are pressed the default action will be ignored
    // this avoids issues such as when pressing the arrow right key the page will scroll to the right etc...
    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-slider': CpSlider;
  }
}
